import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import DiagnosticReport = fhir.DiagnosticReport;
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';

export const useStyles = makeStyles((theme: Theme) => ({
  draftNote: {
    color: theme.palette.text.hint,
  },
  menusContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  menusItems: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

const messages = defineMessages({
  variantSelectionOptions: {
    id: 'actionsContainer.variantActions.options',
    defaultMessage: 'Variant Selection Options',
  },
  variantSelectionAnchorText: {
    id: 'actionsContainer.variantActions.anchor',
    defaultMessage: 'Select Note',
  },
  createDraftReport: {
    id: 'actionsContainer.variantActions.createDraftReport',
    defaultMessage: 'Create draft note',
  },
  emptyReportsTitle: {
    id: 'actionsContainer.variantActions.emptyReports.title',
    defaultMessage: 'No notes found',
  },
  genomicReportTitle: {
    id: 'actionsContainer.variantActions.genomicReportTitle',
    defaultMessage: '<note>{status}</note> {date} MTB Notes ',
  },
  genomicsReportPreview: {
    id: 'actionsContainer.variantActions.genomicsReportPreview',
    defaultMessage: '{count} variants targeted',
  },
});

interface VariantSelectionActionsProps {
  genomicsReports?: DiagnosticReport[];
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectGenomicsReport: (DiagnosticReport) => Promise<void>;
  newDraft: boolean;
  setNewDraft: (status: boolean) => void;
}

export const VariantSelectionActions: React.FC<
  VariantSelectionActionsProps
> = ({ genomicsReports, selectGenomicsReport }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const [selectedGenomicReportId, setSelectedGenomicReportId] =
    React.useState<string>();
  const [selectionMade, setSelectionMade] = React.useState(false);

  React.useEffect(() => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (genomicsReports.length && !selectionMade) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setSelectedGenomicReportId(genomicsReports[0].id);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      selectGenomicsReport(genomicsReports[0]);
    }
  }, [
    genomicsReports,
    setSelectedGenomicReportId,
    selectGenomicsReport,
    selectionMade,
  ]);

  const onExistingReportClick = async (diagnosticReport: DiagnosticReport) => {
    setSelectedGenomicReportId(diagnosticReport.id);
    await selectGenomicsReport(diagnosticReport);
  };

  // Add existing reports:
  const items = genomicsReports?.map(diagnosticReport => {
    return (
      <SelectOption
        className={classes.menusItems}
        key={`mtb-report-${diagnosticReport.id}`}
        title={
          formatMessage(messages.genomicReportTitle, {
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            date: new Date(diagnosticReport.effectiveDateTime).toDateString(),
            status: diagnosticReport.status === 'final' ? '' : '(Draft)',
            note: str => <i className={classes.draftNote}>{str}</i>,
          }) as string
        }
        subtitle={formatMessage(messages.genomicsReportPreview, {
          count: diagnosticReport?.result?.length || 0,
        })}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        value={diagnosticReport.id}
      />
    );
  });
  const emptyItems = [
    <SelectOption
      key="empty-notes"
      title={formatMessage(messages.emptyReportsTitle)}
      value={formatMessage(messages.variantSelectionAnchorText)}
      disabled={true}
    />,
  ];

  return (
    <Select
      label=""
      placeholder={formatMessage(messages.variantSelectionAnchorText)}
      aria-label={formatMessage(messages.variantSelectionAnchorText)}
      onChange={id => {
        setSelectedGenomicReportId(id);
        setSelectionMade(true);
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        const diagnosticReport = genomicsReports.find(report => {
          return report.id === id;
        });
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onExistingReportClick(diagnosticReport);
      }}
      value={selectedGenomicReportId}
      className={classes.menusContainer}
    >
      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
      {genomicsReports.length ? items : emptyItems}
    </Select>
  );
};
